import { useCallback, useMemo } from 'react';

import { Box } from '#/components/Box/Box';
import { BulletList } from '#/components/BulletList/BulletList';
import { Icon } from '#/components/Icon/Icon';
import { InfoList } from '#/components/InfoList/InfoList';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { DynamicSlideshow } from '#/components/Slideshow/DynamicSlideshow';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ListingViewSection } from '#/modules/listing/components/ListingView/Section/ListingViewSection';
import { OrgListingsTabs } from '#/modules/listing/components/OrgListingTabs/OrgListingsTabs';
import {
  listingRichTextTruncateAtAbout8Lines,
  ListingRichTextTruncated,
} from '#/modules/listing/components/RichText/Truncated/ListingRichTextTruncated';
import type { ApiOrg } from '#/modules/listing/orgs/api/types/ApiOrg';
import type { DatetimeString } from '#/types/TypedDateString';
import { trackAnchorClicked } from '#/utils/analytics/track/trackAnchorClicked';
import { AREAS_OF_FOCUS } from '#/utils/constants/general/areasOfFocus';
import { showModal } from '#/zustand-stores/modalStore';

import { orgHasListings } from './utils/orgHasListings';
import type { ViewOrgPageTabSlug } from './ViewOrgPageTabSlug';

const SLIDESHOW_ITEM_SIZE = 200;

type Props = {
  org: ApiOrg;
  tab: ViewOrgPageTabSlug;
  now: DatetimeString;
};

export function ViewOrgPageDetails({ org, tab, now }: Props) {
  const { i18n } = useI18n();

  const headerTitle = useMemo(() => {
    const hasPhoto = org.slideshowItems.find((item) => item.type === 'IMAGE');
    const hasVideo = org.slideshowItems.find((item) => item.type === 'VIDEO');

    const orgName = org.name;

    if (hasPhoto && !hasVideo) {
      return i18n('Photos for %(orgName)s', { orgName });
    }

    if (hasVideo && !hasPhoto) {
      return i18n('Videos for %(orgName)s', { orgName });
    }

    return i18n('Photos & Videos for %(orgName)s', { orgName });
  }, [i18n, org.name, org.slideshowItems]);

  const openMediaGallery = useCallback(
    ({ itemIndex }: { itemIndex: number }) => {
      showModal(
        'MEDIA_GALLERY',
        // @ts-expect-error TS(2322): Type 'SlideshowData[]' is not assignable to type '... Remove this comment to see the full error message
        { items: org.slideshowItems, itemIndex },
      );
    },
    [org.slideshowItems],
  );

  return (
    <Box px={0} mb={2}>
      {org.mission !== null && org.missionTruncated !== null && (
        <ListingViewSection id="mission" title={i18n('Mission')}>
          <ListingRichTextTruncated
            html={org.mission}
            truncateAtHeight={
              org.missionTruncated === org.mission
                ? null
                : listingRichTextTruncateAtAbout8Lines
            }
            showMoreLinkTitle={i18n('Read more about this organization')}
            showLessLinkTitle={i18n('Read less about this organization')}
          />
        </ListingViewSection>
      )}
      {org.description !== null && org.descriptionTruncated !== null && (
        <ListingViewSection id="about" title={i18n('About Us')}>
          <ListingRichTextTruncated
            html={org.description}
            truncateAtHeight={
              org.description === org.descriptionTruncated
                ? null
                : listingRichTextTruncateAtAbout8Lines
            }
            showMoreLinkTitle={i18n('Read more about this organization')}
            showLessLinkTitle={i18n('Read less about this organization')}
          />
        </ListingViewSection>
      )}
      {org.areasOfFocusKeys.length > 0 && (
        <ListingViewSection title={i18n('Cause Areas Include')}>
          <BulletList
            columns={2}
            itemMargin={32}
            items={org.areasOfFocusKeys.map((area) => ({
              key: area,
              value: AREAS_OF_FOCUS(i18n)[area],
            }))}
          />
        </ListingViewSection>
      )}
      <ListingViewSection
        title={org.phone ? i18n('Location & Contact') : i18n('Location')}
      >
        <InfoList $columns={1} $noSvgStyles>
          {org.phone && (
            <li>
              <Box display="inline-flex" mr="6px">
                <Icon name="cell-phone" size={24} />
              </Box>
              <StyledNativeLinkWithRef
                href={`tel:${org.phone.replace(/[^0-9+]/g, '')}`}
                onClick={(event) =>
                  trackAnchorClicked(event, 'Organization phone number', {
                    method: 'Phone',
                  })
                }
              >
                {org.phone}
              </StyledNativeLinkWithRef>
            </li>
          )}

          {org.address && (
            <li>
              <Box display="inline-flex" mr="6px">
                <Icon name="location" size={24} />
              </Box>

              <div>
                {org.address.full}
                {org.address.line2 && <br />}
                {org.address.line2}
              </div>
            </li>
          )}
        </InfoList>
      </ListingViewSection>

      {org.slideshowItems.length > 0 && (
        <ListingViewSection title={headerTitle}>
          <div
            style={
              org.slideshowItems.length === 1
                ? // Reduce width so the only item present is left aligned
                  { width: SLIDESHOW_ITEM_SIZE }
                : { margin: '0 64px' }
            }
          >
            <DynamicSlideshow
              carouselId="org-photos-carousel"
              items={org.slideshowItems}
              qaId="org-carousel"
              onClickIndex={openMediaGallery}
              size={SLIDESHOW_ITEM_SIZE}
            />
          </div>
        </ListingViewSection>
      )}

      {orgHasListings(org) && (
        <div id="opportunities" data-qa-id="opportunities">
          <ListingViewSection title={i18n('Current listings')}>
            <OrgListingsTabs
              org={org}
              tab={tab}
              trackMobileOpen="Listing Type Tab on Org Page"
              trackDesktopOpen="Listing Type Tab on Org Page (Mobile)"
              trackShowMore="View More Listings on Org Page"
              trackClickedListing="Current Listings on Org Page"
              now={now}
            />
          </ListingViewSection>
        </div>
      )}
    </Box>
  );
}
